<template>
  <div class="od-wrap">
    <div class="od-container">
      <div v-if="loading" class="od-spinner-wrap">
        <div class="od-spinner" />
      </div>

      <div v-else-if="!order" class="od-empty">
        <div class="od-empty-icon">
          <a-icon type="inbox" />
        </div>
        <h3>Pesanan Tidak Ditemukan</h3>
        <p>Pesanan yang Anda cari tidak dapat ditemukan atau tidak dapat diakses.</p>
      </div>

      <div v-else>
        <div
          v-if="order.order_state === 'CANCELREQUESTED' || order.order_state === 'CANCELED'"
          class="od-cancel-banner"
        >
          <a-icon type="close-circle" class="od-cancel-banner-icon" />
          <div>
            <h4>{{ order.order_state === 'CANCELREQUESTED' ? 'Cancel Requested' : 'Pesanan Dibatalkan' }}</h4>
            <p>Alasan: <strong>{{ order.reason || order.cancelled_reason || '-' }}</strong></p>
          </div>
        </div>

        <div class="od-grid">
          <div class="od-main">
            <h1 class="od-page-title">
              Detail Pesanan
            </h1>

            <order-timeline :order="order" />

            <order-purchase-info
              :order="order"
              :is-guest="false"
              :to-currency="toCurrency"
              :format-date="formatDate"
              :get-purchase-status-class="getPurchaseStatusClass"
              :get-payment-status-label="getPaymentStatusLabel"
              :copy-to-clipboard="copyToClipboard"
            />

            <order-product-list
              :order="order"
              :to-currency="toCurrency"
            />

            <order-payment-info
              :order="order"
              :mix-payment-data="mixPaymentData"
              :is-paid="isPaid"
              :to-currency="toCurrency"
              :get-purchase-status-class="getPurchaseStatusClass"
              :get-payment-status-label="getPaymentStatusLabel"
              :copy-to-clipboard="copyToClipboard"
              @showProof="openProof"
            />

            <order-delivery-info
              v-if="order.shipment_data && order.shipment_data.courier"
              :order="order"
              :copy-to-clipboard="copyToClipboard"
            />
          </div>

          <div class="od-side">
            <order-bonus-section
              v-if="potensialPromotion && potensialPromotion.length"
              :potensial-promotion="potensialPromotion"
              :to-currency="toCurrency"
            />
            <order-summary-side
              :order="order"
              :is-guest="false"
              :shipping-cost="shippingCost"
              :insurance-cost="insuranceCost"
              :discount-transaction="discountTransaction"
              :discount-product="discountProduct"
              :voucher-used="voucherUsed"
              :is-paid="isPaid"
              :to-currency="toCurrency"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showLightbox" class="od-lightbox" @click.self="showLightbox = false">
      <button class="od-lightbox-close" @click="showLightbox = false">
        <a-icon type="close" />
      </button>
      <img :src="lightboxSrc" />
    </div>
  </div>
</template>

<script>
import './order-detail.css'
import { getSalesInternalOrderDetail } from '@/api/channels/distributor'
import { getInternalFulfillmentByOrder } from '@/api/fulfillment'
import apiClient from '@/services/axios'
import OrderTimeline from './OrderTimeline.vue'
import OrderPurchaseInfo from './OrderPurchaseInfo.vue'
import OrderProductList from './OrderProductList.vue'
import OrderPaymentInfo from './OrderPaymentInfo.vue'
import OrderDeliveryInfo from './OrderDeliveryInfo.vue'
import OrderSummarySide from './OrderSummarySide.vue'
import OrderBonusSection from './OrderBonusSection.vue'
import { notification } from 'ant-design-vue'

export default {
  name: 'PublicOrderDetail',
  components: {
    OrderTimeline,
    OrderPurchaseInfo,
    OrderProductList,
    OrderPaymentInfo,
    OrderDeliveryInfo,
    OrderSummarySide,
    OrderBonusSection,
  },
  data() {
    return {
      loading: true,
      order: null,
      mixPaymentData: null,
      potensialPromotion: [],
      discountTransaction: [],
      discountProduct: [],
      voucherUsed: [],
      showLightbox: false,
      lightboxSrc: null,
    }
  },
  computed: {
    orderId() {
      return this.$route.params.id
    },
    isPaid() {
      const status = this.order && this.order.payment_state && this.order.payment_state.toUpperCase()
      return status === 'PAID' || status === 'VERIFIED'
    },
    shippingCost() {
      if (!this.order) return 0
      const sd = this.order.shipment_data || {}
      return (
        (sd.shipping_cost && sd.shipping_cost.value) ||
        sd.shipping_fee ||
        this.order.shipping_cost ||
        0
      )
    },
    insuranceCost() {
      if (!this.order) return 0
      const sd = this.order.shipment_data || {}
      return (
        (sd.insurance_cost && sd.insurance_cost.value) ||
        sd.insurance_fee ||
        this.order.insurance_cost ||
        0
      )
    },
  },
  mounted() {
    this.fetchOrder()
  },
  methods: {
    toCurrency(value) {
      let currency = (this.order && this.order.currency) || 'IDR'
      if (currency === 'Rp') currency = 'IDR'
      try {
        return new Intl.NumberFormat('id-ID', {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0)
      } catch {
        return `${currency} ${(value || 0).toLocaleString('id-ID')}`
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        const d = new Date(dateString)
        return d.toLocaleString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return dateString
      }
    },

    getPurchaseStatusClass(status) {
      const s = (status || '').toUpperCase()
      if (s === 'PAID' || s === 'VERIFIED') return 'od-badge-green'
      if (s === 'UNPAID' || s === 'WAITING_PAYMENT' || s === 'PENDING') return 'od-badge-yellow'
      if (s === 'CANCELED' || s === 'REJECTED') return 'od-badge-red'
      return 'od-badge-gray'
    },

    getPaymentStatusLabel(status) {
      if (!status) return 'Belum Bayar'
      const s = status.toUpperCase()
      if (s === 'PAID' || s === 'VERIFIED') return 'Lunas'
      if (s === 'UNPAID' || s === 'WAITING_PAYMENT' || s === 'WAITING_CONFIRMATION' || s === 'PENDING') return 'Menunggu Pembayaran'
      if (s === 'CANCELED' || s === 'REJECTED') return 'Dibatalkan'
      return status
    },

    async copyToClipboard(text, label) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        notification.success({ message: `${label || 'Teks'} berhasil disalin` })
      } catch {
        notification.error({ message: 'Gagal menyalin' })
      }
    },

    openProof(src) {
      this.lightboxSrc = src
      this.showLightbox = true
    },

    async fetchOrder() {
      this.loading = true
      try {
        const orderId = this.orderId

        let orderData = {}
        try {
          const res = await getSalesInternalOrderDetail({ order_id: orderId })
          orderData = res.data || {}
          if (orderData.data) orderData = orderData.data
        } catch (e) {
          console.error('Failed to fetch order detail:', e)
        }
        
        const channelId = orderData.channel_id || this.$route.params.channel_id

        let fulfillmentData = {}
        try {
          const fRes = await getInternalFulfillmentByOrder({
            order_id: orderId,
            workspace_id: this.$route.query.workspace_id,
          })
          fulfillmentData = fRes.data || {}
          if (fulfillmentData.data) fulfillmentData = fulfillmentData.data
        } catch (e) {
          console.error('Failed to fetch fulfillment detail:', e)
        }

        const fulfillment = (fulfillmentData.fulfillments && fulfillmentData.fulfillments.length > 0) ? fulfillmentData.fulfillments[0] : {}
        const shippingInfo = fulfillment.shipping || {}
        const destInfo = fulfillment.destination || {}
        const fulfillmentLineItems = fulfillment.line_items || []

        if (orderData && orderData.payment_data && orderData.payment_data.mix_payment_method_id) {
          try {
            const mixRes = await apiClient({ url: `/channel/distributor/payment_provider/query/mix-payment/${channelId}/${orderId}`, method: 'GET' })
            this.mixPaymentData = mixRes.data
          } catch { /* silent */ }
        }

        if (orderData) {
          if (orderData.promotions) {
            const promos = orderData.promotions
            const allDiscounts = promos.discounts || []

            const transDiscounts = allDiscounts.filter(v => v.type && v.type.toLowerCase() === 'transaction')
            this.discountTransaction = transDiscounts.flatMap(transEntry => {
              if (transEntry.data) {
                return transEntry.data.flatMap(d => (d.promotion_data || []).map(p => ({
                  name: p.name || 'Promo Transaksi',
                  discount: p.value || 0,
                })))
              }
              return {
                name: transEntry.code || 'Promo Transaksi',
                discount: transEntry.value || 0,
              }
            })

            const prodDiscounts = allDiscounts.filter(v => v.type && v.type.toLowerCase() === 'product')
            this.discountProduct = prodDiscounts.flatMap(prodEntry => {
              if (prodEntry.data) {
                return prodEntry.data.flatMap(d => (d.promotion_data || []).map(p => ({
                  name: p.name || 'Promo Produk',
                  discount: p.value || 0,
                })))
              }
              return {
                name: prodEntry.code || 'Promo Produk',
                discount: prodEntry.value || 0,
              }
            })

            this.voucherUsed = (promos.vouchers_used && promos.vouchers_used.flatMap(v => {
              if (!v.data) {
                return {
                  voucher_code: v.name || v.voucher_code || v.code || 'Voucher',
                  discount_amount: v.voucher_amount_used || v.value || v.discount || 0,
                }
              }
              return (v.data || []).flatMap(d => {
                return (d.voucher_used_data || []).map(detail => ({
                  voucher_code: detail.name || detail.voucher_code || detail.code || 'Voucher',
                  discount_amount: detail.voucher_amount_used || detail.value || detail.discount || 0,
                }))
              })
            })) || []

            const mappedPotensial = []
            ;(promos.coins || []).forEach(dataCoin => {
              ;(dataCoin.data || []).forEach(coinData => {
                ;(coinData.coin_data || []).forEach(data => {
                  ;(data.gift || []).forEach(dataGift => {
                    mappedPotensial.push({ ...data, ...dataGift, gift_type: 'coin' })
                  })
                })
              })
            })
            ;(promos.vouchers || []).forEach(dataVoucher => {
              ;(dataVoucher.data || []).forEach(data => {
                ;(data.gift || []).forEach(dataGift => {
                  mappedPotensial.push({ ...dataGift, name: data.name, gift_type: 'voucher' })
                })
              })
            })

            if (orderData.promotion_items && orderData.promotion_items.length > 0) {
              const promotionIds = [...new Set(orderData.promotion_items.map(p => p.promotion_id))]
              promotionIds.forEach(id => {
                const dataPromotion = { gift_type: 'product', name: '', promotion_id: id, gift: [] }
                orderData.promotion_items.forEach(promotion => {
                  if (promotion.promotion_id === id) {
                    const gift = [{
                      catalog: { id: promotion.catalog_id, sku: promotion.catalog_sku, title: promotion.catalog_title },
                      catalog_id: promotion.catalog_id, qty: promotion.quantity, type: promotion.type, uom: promotion.uom,
                    }]
                    dataPromotion.name = promotion.name || 'Produk Bonus'
                    dataPromotion.uom = promotion.reference_uom
                    dataPromotion.gift.push(...gift)
                  }
                })
                if (!mappedPotensial.find(p => p.promotion_id === id)) {
                  mappedPotensial.push(dataPromotion)
                }
              })
            }
            this.potensialPromotion = mappedPotensial
          }

          this.order = {
            ...orderData,
            payment_proof_url: orderData.payment_proof_url || null,
            billing_data: (() => {
              const b = orderData.billing || {}
              const addr = b.address
              const addrObj = typeof addr === 'object' ? addr : null
              return {
                ...b,
                name: b.name || orderData.buyer_name || orderData.customer_name || orderData.name,
                email: b.email || (orderData.customer_info && orderData.customer_info.email) || orderData.buyer_email || orderData.customer_email || orderData.email || (orderData.buyer_data && orderData.buyer_data.email),
                address: addrObj ? (addrObj.address_line1 || addrObj.address || '') : (typeof addr === 'string' ? addr : ''),
                phone: b.phone || orderData.buyer_phone || orderData.customer_phone || orderData.phone,
                city: b.city || (addrObj && addrObj.city) || '',
                province: b.province || (addrObj && addrObj.province) || '',
                postal_code: b.postal_code || (addrObj && addrObj.postal_code) || '',
              }
            })(),
            order_state: orderData.order_state || orderData.sales_state || orderData.status || 'SUBMITTED',
            payment_state: orderData.payment_state || orderData.payment_status,
            shipping_state: fulfillment.shipping_state || orderData.shipping_state || orderData.shipping_status,
            sub_total: orderData.sub_total || orderData.subtotal || 0,
            gross_total: (() => {
              let total = orderData.gross_total || orderData.order_total || orderData.total_value || orderData.total || 0;
              const sub = orderData.sub_total || orderData.subtotal || 0;
              const disc = orderData.discount || 0;
              const loyalty = orderData.loyalty_usage || 0;
              const ship = (shippingInfo.shipping_cost && shippingInfo.shipping_cost.value) || 0;
              const ins = (shippingInfo.insurance_cost && shippingInfo.insurance_cost.value) || 0;
              const platform = orderData.payment_fee || 0;
              if (total === sub && (disc > 0 || loyalty > 0 || ship > 0 || ins > 0 || platform > 0)) {
                return total + ship + ins + platform - disc - loyalty;
              }
              return total;
            })(),
            destination_data: orderData.destination_data || {
              name: destInfo.name || orderData.buyer_name || orderData.customer_name || orderData.name,
              address: (destInfo.address && destInfo.address.line1) || orderData.delivery_address || orderData.address,
              phone: destInfo.phone || orderData.buyer_phone || orderData.customer_phone || orderData.phone,
              sub_district: (destInfo.address && destInfo.address.sub_district) || orderData.sub_district,
              district: (destInfo.address && destInfo.address.district) || orderData.district,
              city: (destInfo.address && destInfo.address.city) || orderData.city,
              province: (destInfo.address && destInfo.address.province) || orderData.province,
              postal_code: (destInfo.address && destInfo.address.postal_code) || orderData.postal_code || orderData.postcode,
            },
            shipment_data: orderData.shipment_data || {
              courier: shippingInfo.courier || orderData.courier || orderData.shipping_method || orderData.shipment_method,
              service_name: shippingInfo.service_name || orderData.service_name || orderData.shipping_service || orderData.shipment_service,
              service_code: shippingInfo.service_code || orderData.service_code,
              awb: shippingInfo.awb || orderData.awb || orderData.tracking_number || orderData.waybill,
              shipping_label_url: shippingInfo.shipping_label_url || orderData.shipping_label_url,
              shipping_cost: shippingInfo.shipping_cost || null,
              insurance_cost: shippingInfo.insurance_cost || null,
            },
            payment_data: orderData.payment_data || {
              term: orderData.payment_method || orderData.payment_term,
              number: '',
            },
            customer_email: (orderData.billing && orderData.billing.email) || (orderData.customer_info && orderData.customer_info.email) || orderData.buyer_email || orderData.customer_email || orderData.email || (orderData.buyer_data && orderData.buyer_data.email),
            items: ((orderData.items || orderData.sales_items) || []).map(item => {
              const allDiscounts = (orderData.promotions && orderData.promotions.discounts) || []
              const productDiscounts = (allDiscounts.find(v => v.type === 'product') || {}).data || []
              const normalizedItem = { ...item, item_id: item.catalog_id || item.item_id || item.id }
              const originalPrice = (item.price && item.price.selling_price) || item.selling_price || item.product_price || (typeof item.price === 'number' ? item.price : 0)
              const match = productDiscounts.find(v => v.catalog_id === normalizedItem.item_id)
              let sellingPrice = originalPrice
              let promoType = null
              let promoValue = 0
              if (match) {
                const qty = item.item_qty || item.qty || item.quantity || 1
                const discountPerUnit = (match.value || 0) / qty
                sellingPrice = originalPrice - discountPerUnit
                const gift = match.promotion_data && match.promotion_data[0] && match.promotion_data[0].gift && match.promotion_data[0].gift[0]
                if (gift) {
                  promoType = gift.value_type
                  promoValue = gift.gift_value || gift.value
                }
              }
              if (!match && item.discount > 0) {
                 const qty = item.item_qty || item.qty || item.quantity || 1
                 const discountPerUnit = item.discount / qty
                 sellingPrice = originalPrice - discountPerUnit
              }
              const matchFulfillmentItem = fulfillmentLineItems.find(f => f.catalog_id === normalizedItem.item_id || f.sku === item.catalog_sku)
              const imgUrl = (matchFulfillmentItem && matchFulfillmentItem.img_url) ? matchFulfillmentItem.img_url : (item.img_url || item.image_url)
              return {
                ...item,
                image_url: imgUrl,
                title: item.title || item.product_name || item.name,
                qty: item.item_qty || item.qty || item.quantity || 0,
                selling_price: Math.round(sellingPrice),
                original_price: originalPrice,
                uom: item.item_uom || item.uom || item.unit || 'pcs',
                promotion_type: promoType,
                product_discount_value: promoValue,
              }
            }),
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
